import React, { useState } from 'react';
import { Wrench, Battery, Truck, Key, AlertTriangle, HelpCircle, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { toast } from 'sonner';

const getServiceIcon = (id) => {
  const icons = {
    'flat-tire': Wrench,
    'battery': Battery,
    'tow': Truck,
    'lockout': Key,
    'check-engine': AlertTriangle,
    'other': HelpCircle
  };
  return icons[id] || HelpCircle;
};

export const ServiceSelection = ({ onNext, onBack }) => {
  const { t } = useTranslation();
  const [selectedService, setSelectedService] = useState('');
  const [otherDetails, setOtherDetails] = useState('');

  const services = [
    {
      id: 'flat-tire',
      name: t('roadside.step2.services.flatTire'),
      description: t('roadside.step2.services.flatTireDesc')
    },
    {
      id: 'battery',
      name: t('roadside.step2.services.battery'),
      description: t('roadside.step2.services.batteryDesc')
    },
    {
      id: 'tow',
      name: t('roadside.step2.services.tow'),
      description: t('roadside.step2.services.towDesc')
    },
    {
      id: 'lockout',
      name: t('roadside.step2.services.lockout'),
      description: t('roadside.step2.services.lockoutDesc')
    },
    {
      id: 'check-engine',
      name: t('roadside.step2.services.checkEngine'),
      description: t('roadside.step2.services.checkEngineDesc')
    },
    {
      id: 'other',
      name: t('roadside.step2.services.other'),
      description: t('roadside.step2.services.otherDesc')
    }
  ];

  const handleServiceSelect = (serviceId) => {
    setSelectedService(serviceId);
    if (serviceId !== 'other') {
      setOtherDetails('');
    }
  };

  const handleNext = () => {
    if (!selectedService) {
      toast.error(t('roadside.errors.selectService'));
      return;
    }

    if (selectedService === 'other' && !otherDetails.trim()) {
      toast.error(t('roadside.errors.describeIssue'));
      return;
    }

    const selectedServiceName = services.find(s => s.id === selectedService)?.name;

    onNext({
      service_type: selectedServiceName,
      notes: selectedService === 'other' ? otherDetails : ''
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-gray-50 pb-safe">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-900">{t('roadside.step2.title')}</h1>
          <p className="text-sm text-gray-600 mt-1">{t('roadside.step2.step')}</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Service Options */}
        {services.map((service) => {
          const Icon = getServiceIcon(service.id);
          const isSelected = selectedService === service.id;

          return (
            <Card
              key={service.id}
              onClick={() => handleServiceSelect(service.id)}
              className={`p-5 cursor-pointer transition-all hover:shadow-lg ${
                isSelected
                  ? 'border-2 border-red-600 bg-red-50 shadow-md'
                  : 'border-2 border-gray-200 hover:border-red-300'
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'bg-red-600' : 'bg-gray-100'
                  }`}
                >
                  <Icon className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-gray-700'}`} />
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-900">{service.name}</h3>
                    {isSelected && (
                      <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center">
                        <ChevronRight className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{service.description}</p>
                </div>
              </div>
            </Card>
          );
        })}

        {/* Other Details */}
        {selectedService === 'other' && (
          <Card className="p-6 border-2 border-red-200 bg-red-50 animate-fade-in">
            <Label className="text-base font-semibold text-gray-900 mb-3 block">
              {t('roadside.step2.otherDetails')}
            </Label>
            <Textarea
              value={otherDetails}
              onChange={(e) => setOtherDetails(e.target.value)}
              placeholder={t('roadside.step2.otherPlaceholder')}
              rows={4}
              className="border-gray-300 focus:border-red-500 focus:ring-red-500 bg-white"
              required
            />
            <p className="text-xs text-gray-600 mt-2">
              {t('roadside.step2.otherHelp')}
            </p>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <Button
            onClick={onBack}
            variant="outline"
            size="lg"
            className="flex-1 border-2 py-6"
          >
            {t('common.back')}
          </Button>
          <Button
            onClick={handleNext}
            disabled={!selectedService || (selectedService === 'other' && !otherDetails.trim())}
            size="lg"
            className="flex-1 bg-red-600 hover:bg-red-700 text-white py-6 text-lg font-semibold shadow-lg"
          >
            {t('common.continue')}
          </Button>
        </div>
      </div>
    </div>
  );
};
